import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import api from '../services/api';

const ProductMaster = () => {
  const { selectedFirmId } = useContext(AuthContext);
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Form State
  const [editingId, setEditingId] = useState(null);
  const [productName, setProductName] = useState('');
  const [productCode, setProductCode] = useState('');
  const [PRate, setPRate] = useState('');
  const [SRate, setSRate] = useState('');
  const [unit, setUnit] = useState('KG');

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchProducts();
  }, [selectedFirmId]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/masters/products?firmId=${selectedFirmId}`);
      setProducts(res.data);
    } catch (err) {
      setError('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const payload = {
      productName,
      productCode: Number(productCode) || 0,
      PRate: Number(PRate) || 0,
      SRate: Number(SRate) || 0,
      unit,
      FirmId: selectedFirmId
    };

    try {
      if (editingId) {
        await api.put(`/masters/products/${editingId}`, payload);
        setSuccess('Product updated successfully');
      } else {
        await api.post('/masters/products', payload);
        setSuccess('Product added successfully');
      }
      resetForm();
      fetchProducts();
    } catch (err) {
      setError('Failed to save product');
    }
  };

  const handleEdit = (p) => {
    setEditingId(p.Id);
    setProductName(p.productName);
    setProductCode(p.productCode || '');
    setPRate(p.PRate || '');
    setSRate(p.SRate || '');
    setUnit(p.unit || 'KG');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      await api.delete(`/masters/products/${id}`);
      setSuccess('Product deleted');
      fetchProducts();
    } catch (err) {
      setError('Failed to delete product');
    }
  };

  const resetForm = () => {
    setEditingId(null);
    setProductName('');
    setProductCode('');
    setPRate('');
    setSRate('');
    setUnit('KG');
  };

  const filteredProducts = products.filter((p) =>
    (p.productName || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="container-fluid py-4">
      <div className="d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom">
        <div>
          <h3 className="fw-bold mb-1 text-primary">
            <i className="bi bi-tag me-2"></i>Product Master (सामान)
          </h3>
          <p className="text-muted mb-0">Manage fish products, purchase/sale rates, and measurement units</p>
        </div>
      </div>

      {error && <div className="alert alert-danger py-2">{error}</div>}
      {success && <div className="alert alert-success py-2">{success}</div>}

      <div className="row g-4">
        {/* ADD / EDIT FORM */}
        <div className="col-12 col-md-4">
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-primary text-white fw-bold">
              {editingId ? 'Edit Product Item' : 'Add New Product Item'}
            </div>
            <div className="card-body">
              <form onSubmit={handleSave}>
                <div className="mb-3">
                  <label className="form-label fw-semibold">Product Name (सामान नांव)</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="e.g. SURMAI, POPLET, BANGDA"
                    value={productName}
                    onChange={(e) => setProductName(e.target.value)}
                    required
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold">Product Code</label>
                  <input
                    type="number"
                    className="form-control"
                    placeholder="0"
                    value={productCode}
                    onChange={(e) => setProductCode(e.target.value)}
                  />
                </div>

                <div className="row g-2 mb-3">
                  <div className="col-6">
                    <label className="form-label fw-semibold">Purchase Rate</label>
                    <input
                      type="number"
                      step="0.01"
                      className="form-control"
                      placeholder="0.00"
                      value={PRate}
                      onChange={(e) => setPRate(e.target.value)}
                    />
                  </div>
                  <div className="col-6">
                    <label className="form-label fw-semibold">Sale Rate</label>
                    <input
                      type="number"
                      step="0.01"
                      className="form-control"
                      placeholder="0.00"
                      value={SRate}
                      onChange={(e) => setSRate(e.target.value)}
                    />
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold">Unit</label>
                  <select className="form-select" value={unit} onChange={(e) => setUnit(e.target.value)}>
                    <option value="KG">KG (Kilogram)</option>
                    <option value="BOX">BOX</option>
                    <option value="NOS">NOS (Pieces)</option>
                    <option value="LITRE">LITRE</option>
                  </select>
                </div>

                <div className="d-flex gap-2">
                  <button type="submit" className="btn btn-primary w-100 fw-bold">
                    <i className="bi bi-check-circle me-1"></i> {editingId ? 'Update' : 'Save'}
                  </button>
                  {editingId && (
                    <button type="button" className="btn btn-outline-secondary" onClick={resetForm}>
                      Cancel
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* PRODUCT LIST */}
        <div className="col-12 col-md-8">
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center">
              <h5 className="fw-bold mb-0 text-dark">
                Product Catalog ({filteredProducts.length})
              </h5>
              <div className="w-50">
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Search product by name..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>
            <div className="table-responsive" style={{ maxHeight: '600px' }}>
              <table className="table table-hover align-middle mb-0">
                <thead className="table-light sticky-top">
                  <tr>
                    <th>#</th>
                    <th>Product Name</th>
                    <th>Code</th>
                    <th>P. Rate</th>
                    <th>S. Rate</th>
                    <th>Unit</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="7" className="text-center py-4 text-muted">
                        <span className="spinner-border spinner-border-sm me-2"></span> Loading product catalog...
                      </td>
                    </tr>
                  ) : filteredProducts.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="text-center py-4 text-muted">
                        No products found.
                      </td>
                    </tr>
                  ) : (
                    filteredProducts.map((p, idx) => (
                      <tr key={p.Id}>
                        <td>{idx + 1}</td>
                        <td className="fw-semibold">{p.productName}</td>
                        <td>{p.productCode || '-'}</td>
                        <td>₹{p.PRate || 0}</td>
                        <td>₹{p.SRate || 0}</td>
                        <td>
                          <span className="badge bg-secondary">{p.unit}</span>
                        </td>
                        <td className="text-end">
                          <button
                            className="btn btn-outline-primary btn-sm me-1"
                            onClick={() => handleEdit(p)}
                          >
                            <i className="bi bi-pencil"></i>
                          </button>
                          <button
                            className="btn btn-outline-danger btn-sm"
                            onClick={() => handleDelete(p.Id)}
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductMaster;
